import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Crown, Star } from "lucide-react";
import { toast } from "sonner";
import { api, SERVING_ROLES } from "@/lib/site";

const empty = {
  full_name: "",
  email: "",
  phone: "",
  address: "",
  date_of_birth: "",
  marital_status: "",
  spouse_name: "",
  children: "",
  accepted_christ: "",
  baptized: "",
  previous_church: "",
  how_heard: "",
  prayer_request: "",
};

const MARITAL = ["Single", "Married", "Divorced", "Widowed"];
const YES_NO = ["Yes", "No", "Not sure"];
const HEARD = ["Friend / Family", "Social Media", "Flyer / Event", "Drove By", "Online Search", "Other"];

export const Membership = () => {
  const [form, setForm] = useState(empty);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (r) =>
    setMinistries((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.address) {
      toast.error("Please fill in your name, email, phone, and address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/membership", { ...form, ministries });
      setDone(true);
      toast.success("Welcome to the family! We've received your application.");
      setForm(empty);
      setMinistries([]);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="membership" data-testid="membership-section" className="relative py-24 sm:py-32 px-6 sm:px-10 bg-[#0A0612] border-y border-[#D4AF37]/15">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-5 flex items-center justify-center gap-2">
            <Crown className="h-4 w-4" /> Join The Family
          </p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0]">
            Become a <span className="gold-text italic">Founding Member</span>
          </h2>
          <p className="text-[#A19CA8] text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            Be part of the very beginning of The Change Center Church. Founding members help lay the
            foundation of this house. Fill out the application below and our team will welcome you personally.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="bg-[#130A1F]/70 backdrop-blur-xl border border-[#D4AF37]/25 p-7 sm:p-10"
        >
          {done ? (
            <div className="text-center py-16" data-testid="membership-success">
              <Star className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
              <h3 className="font-display text-3xl text-[#F9F6F0] mb-3">Welcome, Founding Member!</h3>
              <p className="text-[#A19CA8] mb-8 max-w-md mx-auto">
                Thank you for taking this step with us. Our team will reach out to you soon.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="membership-another-btn"
                className="border-2 border-[#D4AF37] text-[#D4AF37] uppercase tracking-wider text-sm px-7 py-3 hover:bg-[#D4AF37]/10 transition-all"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="membership-form">
              <Group title="Personal Information">
                <Field label="Full Name" required>
                  <input data-testid="member-full-name" value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" className="ccc-input" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email" required>
                    <input data-testid="member-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="ccc-input" />
                  </Field>
                  <Field label="Phone" required>
                    <input data-testid="member-phone" value={form.phone} onChange={update("phone")} placeholder="(404) 000-0000" className="ccc-input" />
                  </Field>
                </div>
                <Field label="Home Address" required>
                  <input data-testid="member-address" value={form.address} onChange={update("address")} placeholder="Street, City, State, ZIP" className="ccc-input" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Date of Birth">
                    <input data-testid="member-dob" type="date" value={form.date_of_birth} onChange={update("date_of_birth")} className="ccc-input" />
                  </Field>
                  <Field label="Marital Status">
                    <select data-testid="member-marital" value={form.marital_status} onChange={update("marital_status")} className="ccc-input">
                      <option value="">Select…</option>
                      {MARITAL.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Spouse Name (if applicable)">
                    <input data-testid="member-spouse" value={form.spouse_name} onChange={update("spouse_name")} placeholder="Spouse's name" className="ccc-input" />
                  </Field>
                  <Field label="Children / Family">
                    <input data-testid="member-children" value={form.children} onChange={update("children")} placeholder="Names & ages (optional)" className="ccc-input" />
                  </Field>
                </div>
              </Group>

              <Group title="Faith Journey">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Have you accepted Christ?">
                    <select data-testid="member-accepted" value={form.accepted_christ} onChange={update("accepted_christ")} className="ccc-input">
                      <option value="">Select…</option>
                      {YES_NO.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Have you been baptized?">
                    <select data-testid="member-baptized" value={form.baptized} onChange={update("baptized")} className="ccc-input">
                      <option value="">Select…</option>
                      {YES_NO.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Previous Church (if any)">
                    <input data-testid="member-previous-church" value={form.previous_church} onChange={update("previous_church")} placeholder="Church name" className="ccc-input" />
                  </Field>
                  <Field label="How did you hear about us?">
                    <select data-testid="member-how-heard" value={form.how_heard} onChange={update("how_heard")} className="ccc-input">
                      <option value="">Select…</option>
                      {HEARD.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
              </Group>

              <Group title="Serving & Prayer">
                <div>
                  <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-3">
                    Ministries You'd Like To Serve In
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-64 overflow-y-auto pr-1" data-testid="member-ministries">
                    {SERVING_ROLES.map((role) => {
                      const active = ministries.includes(role);
                      return (
                        <button
                          type="button"
                          key={role}
                          onClick={() => toggle(role)}
                          data-testid={`ministry-${role.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                          className={`flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm border transition-all duration-200 ${
                            active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F9F6F0]" : "border-[#D4AF37]/20 text-[#A19CA8] hover:border-[#D4AF37]/50"
                          }`}
                        >
                          <span className={`h-4 w-4 shrink-0 border flex items-center justify-center ${active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#D4AF37]/50"}`}>
                            {active && <Check className="h-3 w-3 text-[#06040A]" />}
                          </span>
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Field label="Prayer Request / Testimony (optional)">
                  <textarea data-testid="member-prayer" value={form.prayer_request} onChange={update("prayer_request")} rows={3} placeholder="Share anything you'd like us to pray about..." className="ccc-input resize-none" />
                </Field>
              </Group>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-membership-btn"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Membership Application"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Group = ({ title, children }) => (
  <div className="space-y-5">
    <h3 className="font-display text-xl text-[#D4AF37] border-b border-[#D4AF37]/15 pb-2">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {children}
  </div>
);
